import 'rxjs/add/observable/of';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/takeWhile';

import * as Cookies from 'js-cookie/src/js.cookie.js';
import * as moment from 'vendors/moment-locale-th';
import * as CryptoJS from 'crypto-js';

import { CompanyFeatureConfigType, FeatureType, SSO } from 'app/model/constant';
import { ActHttpService, ContentTypes, HeaderKey, HeaderTypes, HeaderValue } from '../act-http/act-http.service';
import { FeatureModel, UserModel } from '../../model/user.model';
import { Headers, Http, RequestOptions, Response, URLSearchParams } from '@angular/http';

import { AppConfigService } from 'app/services/app-config/app-config.service';
import { Injectable } from '@angular/core';
import { KJUR } from 'jsrsasign/lib/jsrsasign.js';
import { Observable } from 'rxjs/Observable';
import { UtilitiesService } from 'app/services/utilities/utilities.service';
import { environment } from '../../../environments/environment';
import { LoginResponseModel } from '../../model/login/login-response.model';
import { AuthorizationResponse, ConsentRequest, AuthorizeRequest, ChangePhoneRequest, ChangePhoneResponse } from 'app/model/authen/authen';
import { redirectURI, domain, configB2C, thresholdExpireToken } from 'environments/properties';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { OTPRequest, RequestOTPResponse, VerifyOTPRequest, VerifyOTPResponse } from 'app/model/otp/otp.model';

export class ActToken {
  accessToken: String;
  refreshToken: String;

  constructor(accessToken?: string, refreshToken?: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
  }
}

@Injectable()
export class AuthenticationService {
  public static readonly features = [
    FeatureType.ALLOW_LIMIT_ROOM,
    FeatureType.ALLOW_LIMIT_CHILD,
    FeatureType.ALLOW_LIMIT_BUDGET,
    FeatureType.ALLOW_SUPERVISOR_APPROVAL,
    FeatureType.ALLOW_EDIT_PROFILE,
    FeatureType.ALLOW_CHANGE_PASSWORD,
    FeatureType.ALLOW_MULTI_PAYMENT,
    FeatureType.ALLOW_MULTI_PAYMENT_CORPORATE_FLEET,
    FeatureType.ALLOW_CREDIT_TERMS,
    FeatureType.ALLOW_BOOK_FLIGHT,
    FeatureType.ALLOW_BOOK_WORK_PURPOSE,
    FeatureType.ALLOW_DISPLAY_PERMISSION_MENU,
    FeatureType.ENABLE_CH2_BOOKER_EMAIL,
    FeatureType.ALLOW_RENT_CAR_CREDIT_TERMS,
    FeatureType.ALLOW_RENT_CAR_OTHER_PAYMENTS,
    FeatureType.ALLOW_SHARE_BUDGET,
    FeatureType.ALLOW_CORPORATE_FLEET,
    FeatureType.ALLOW_BOOK_FOR_TRAVELLER,
    FeatureType.ENABLE_PIN,
    FeatureType.ENABLE_PRE_APPROVAL,
  ];

  private readonly expiresTime = 365;
  private jsRsaSign = new KJUR.jws.JWS();
  private refreshingToken = false;

  private featuresSource = new BehaviorSubject([]);
  currentFeatures = this.featuresSource.asObservable();
  private companyNameSource = new BehaviorSubject({ companyTitleTh: '', companyTitleEn: '' });
  currentCompanyName = this.companyNameSource.asObservable();


  constructor(
    private appConfigService: AppConfigService,
    private utilitiesService: UtilitiesService,
    private http: Http
  ) { }

  saveToken(logInResponse: LoginResponseModel) {
    if (logInResponse.token && logInResponse.token.accessToken && logInResponse.token.refreshToken) {
      Cookies.remove(AppConfigService.tokenCookieName, { domain: domain });
      Cookies.set(AppConfigService.tokenCookieName, this.utilitiesService.encodeToCookie(JSON.stringify(logInResponse.token)), { expires: this.expiresTime, domain: domain });
    }
    if (logInResponse.user) {
      logInResponse.user.features = [];
      this.saveUserInformationCookie(logInResponse.user);
      if (logInResponse.user.userSetting) {
        this.saveLanguageCurrencyCookie(logInResponse.user.userSetting.lang, logInResponse.user.userSetting.currency);
      }
    }
  }

  saveLanguageCurrencyCookie(lang: string, currency: string) {
    Cookies.set(AppConfigService.languageCookieName, lang, { domain: domain });
    Cookies.set(AppConfigService.currencyCookieName, currency, { domain: domain });
  }

  saveUserInformationCookie(user: UserModel) {
    const userCopy = { ...user };
    delete userCopy.userSetting;
    Cookies.remove(AppConfigService.userInfoCookieName, { domain: domain });
    Cookies.set(AppConfigService.userInfoCookieName, this.utilitiesService.encodeToCookie(JSON.stringify(userCopy)), { expires: this.expiresTime, domain: domain });
  }

  savePromotionToken(logInResponse: LoginResponseModel) {
    Cookies.remove(AppConfigService.tokenCookieName, { domain: domain });
    Cookies.set(AppConfigService.tokenCookieName, this.utilitiesService.encodeToCookie(JSON.stringify(logInResponse.token)), { expires: this.expiresTime, path: '/promotion', domain: domain });
    Cookies.remove(AppConfigService.userInfoCookieName, { domain: domain });
    Cookies.set(AppConfigService.userInfoCookieName, this.utilitiesService.encodeToCookie(JSON.stringify(logInResponse.user)), { expires: this.expiresTime, path: '/promotion', domain: domain });
  }

  updateToken(newToken: ActToken): boolean {
    const user = this.getUserInfo();
    if (user && newToken && newToken.accessToken && newToken.refreshToken) {
      const loginResponseModel = new LoginResponseModel(user, newToken);
      this.saveToken(loginResponseModel);
      return true;
    }
    return false;
  }

  getPublicKey(): Observable<any> {
    const publicKey = this.utilitiesService.decodeFromCookie(Cookies.get(AppConfigService.publicKeyCookieName));
    if (publicKey) {
      return Observable.of(publicKey);
    }
    return this.http
      .get(environment.publicKey, this.getRequestOptions(HeaderTypes.EXCLUDE_TOKEN))
      .map((res: Response) => res.text())
      .flatMap(responsePublicKey => {
        Cookies.set(AppConfigService.publicKeyCookieName, this.utilitiesService.encodeToCookie(responsePublicKey), { expires: this.expiresTime, domain: domain });
        return Observable.of(responsePublicKey);
      });
  }

  isJWTFormat(token: string): Observable<boolean> {
    try {
      this.jsRsaSign.parseJWS(token);
      return Observable.of(true);
    } catch (error) {
      return Observable.of(false);
    }
  }

  isValidSignature(token: string, publicKey: any): Observable<boolean> {
    try {
      return KJUR.jws.JWS.verify(token, publicKey, ['RS256']) ? Observable.of(true) : Observable.of(false);
    } catch (error) {
      return Observable.of(false);
    }
  }

  isNotExpired(token: string): Observable<boolean> {
    const tokenArray = this.decodeJWT(token);
    const payload = tokenArray[1];
    const expAt = moment.unix(payload['exp']);
    const now = moment();
    return expAt.diff(now) > thresholdExpireToken ? Observable.of(true) : Observable.of(false);
  }

  isB2CClient() {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    const tokenArray = this.decodeJWT(token.accessToken.valueOf());
    const payload = tokenArray[1];
    return payload['clientId'] === configB2C.clientId;
  }

  refreshToken(actToken: ActToken): Observable<boolean> {
    if (this.refreshingToken) {
      return Observable.of(true);
    }

    if (actToken && actToken.refreshToken) {
      this.refreshingToken = true;
      const userInfo = this.getUserInfo();
      if (userInfo && userInfo.companyCode) {
        return Observable.of(false);
      }
      return this.http.get(environment.endpoint.refreshToken, this.getRequestOptions(HeaderTypes.ACT_TOKEN))
        .flatMap((response: Response) => {
          const result = response.json();
          const newActToken = new ActToken(result.data.token.accessToken, result.data.token.refreshToken);
          return this.updateToken(newActToken) ? Observable.of(true) : Observable.of(false);
        })
        .flatMap((refreshResult => {
          return refreshResult ? this.saveUserFeature(this.getToken(), userInfo, AuthenticationService.features) : Observable.of(false);
        }))
        .flatMap(() => {
          return this.refreshTokenSso(this.getSsoToken());
        })
        .catch(() => {
          this.refreshingToken = false;
          return Observable.of(false);
        });
    } else {
      Cookies.remove(AppConfigService.tokenCookieName, { domain: domain });
      Cookies.remove(AppConfigService.userInfoCookieName, { domain: domain });
      Cookies.remove(AppConfigService.featureCookieName, { domain: domain });
      Cookies.remove(AppConfigService.featureCompanyCookieName, { domain: domain });
      return Observable.of(false);
    }
  }

  refreshTokenSso(actToken: ActToken): Observable<boolean> {
    if (actToken && actToken.refreshToken) {
      return this.http.get(environment.endpoint.refreshToken, this.getRequestOptions(HeaderTypes.SSO_TOKEN))
        .map((response: Response) => response.json().data)
        .flatMap(result => {
          const newActToken = new ActToken(result.token.accessToken, result.token.refreshToken);
          this.saveSsoToken(newActToken);
          this.refreshingToken = false;
          return Observable.of(true);
        })
        .catch((error) => {
          console.error('refreshTokenSso failed:' + error);
          this.refreshingToken = false;
          return Observable.of(true);
        });
    }
  }

  getUserFeature(actToken: ActToken, user: UserModel, featureNames: string[], updateCurrentFeatures = false, shareBudgetCode?: string): Observable<any[]> {
    if (user.companyTitleEn && user.companyTitleTh) {
      this.companyNameSource.next({ companyTitleEn: user.companyTitleEn, companyTitleTh: user.companyTitleTh });
    }

    const queryParams = new URLSearchParams();
    featureNames && featureNames.length > 0 && queryParams.append(`features`, featureNames.join(','));
    queryParams.append(`companyId`, user.companyId.toString());
    shareBudgetCode && queryParams.append(`shareBudgetCode`, shareBudgetCode);

    return this.validateToken(this.getToken()).flatMap((res) => {
      if (res) {
        const requestOption = this.getRequestOptions(HeaderTypes.ACT_TOKEN);
        requestOption.params = queryParams;
        return this.http.get(environment.endpoint.getFeaturesActAuthz, requestOption)
          .map((response: Response) => {
            if (updateCurrentFeatures) {
              this.featuresSource.next(response.json().data);
            }
            return response.json().data;
          });
      } else {
        this.clearToken();
        this.utilitiesService.redirectPage('/', this.appConfigService.getLanguage());
        throw new Error(ActHttpService.unauthorizeMessage);
      }
    });
  }

  getUserFeatureAsync(actToken: ActToken, user: UserModel, featureNames: string[]) {
    const queryParams = new URLSearchParams();
    featureNames && featureNames.length > 0 && queryParams.append(`features`, featureNames.join(','));
    queryParams.append(`companyId`, user.companyId.toString());

    const requestOption = this.getRequestOptions(HeaderTypes.ACT_TOKEN);
    requestOption.params = queryParams;
    return this.http.get(environment.endpoint.getFeaturesActAuthz, requestOption)
      .map((res: Response) => {
        return res.json().data;
      }).toPromise();
  }

  saveUserFeature(actToken: ActToken, user: UserModel, featureNames: string[]): Observable<boolean> {
    return this.getUserFeature(actToken, user, featureNames, true).flatMap((featureResult) => {
      if (featureResult && featureResult.length > 0) {
        this.featuresSource.next(featureResult);
        const userFeatures = <FeatureModel[]>this.getUserFeatureFromFeature(featureResult);
        const companyFeatures = this.getCompanyFeatureFromFeature(featureResult);
        if (userFeatures && userFeatures.length > 0) {
          const featuresAllowLimitBudget = userFeatures.find(f => f.feature === FeatureType.ALLOW_LIMIT_BUDGET);
          const featuresAllowBookForTraveller = userFeatures.find(f => f.feature === FeatureType.ALLOW_BOOK_FOR_TRAVELLER);
          if (featuresAllowLimitBudget && featuresAllowLimitBudget.data && featuresAllowLimitBudget.data.travelPolicyList) {
            featuresAllowLimitBudget.data.travelPolicyList = undefined;
          }
          if (featuresAllowBookForTraveller && featuresAllowBookForTraveller.data) {
            featuresAllowBookForTraveller.data = undefined;
          }
          Cookies.set(AppConfigService.featureCookieName, this.utilitiesService.encodeToCookie(JSON.stringify(userFeatures)), { expires: this.expiresTime, domain: domain });
        }
        if (companyFeatures && companyFeatures.length > 0) {
          companyFeatures.forEach((feature, index) => {
            if (feature.feature === FeatureType.CONFIG_NOTIFY_TERMS_CONDITIONS_AFTER_LOGIN) {
              feature.disabled = false;
              delete feature.data['popupMessageTH'];
              delete feature.data['popupMessageEN'];
              Cookies.set(AppConfigService.notifyDisabledCookieName, this.utilitiesService.encodeToCookie(JSON.stringify([feature])), { expires: this.expiresTime, domain: domain });
              companyFeatures.splice(index, 1);
              return;
            }
          });
          companyFeatures.length > 0 && Cookies.set(AppConfigService.featureCompanyCookieName,
            this.utilitiesService.encodeToCookie(JSON.stringify(companyFeatures)), { expires: this.expiresTime, domain: domain });
        }
        return Observable.of(true);
      }
      return Observable.of(false);
    }).catch(() => Observable.of(null));
  }

  decodeJWT(token: string): any[] {
    return token ? token.split(`.`, 2).map(item => JSON.parse(this.utilitiesService.base64Decoder(item))) : undefined;
  }

  validateToken(actToken: ActToken): Observable<boolean> {
    if (actToken && actToken.accessToken) {
      return this.isJWTFormat(actToken.accessToken.valueOf())
        .flatMap(isJWTFormatResult => {
          return isJWTFormatResult ? this.getPublicKey() : Observable.throw('Invalid JWTFormat');
        })
        .flatMap(publicKey => {
          return publicKey ? this.isValidSignature(actToken.accessToken.valueOf(), publicKey) : Observable.throw('Invalid PublicKey');
        })
        .flatMap(isValidSignature => {
          return isValidSignature ? this.isNotExpired(actToken.accessToken.valueOf()) : Observable.throw('Invalid Signature');
        })
        .flatMap(isAlive => {
          return isAlive ? Observable.of(true) : this.refreshToken(actToken).toPromise();
        })
        .catch((error) => {
          console.error(error);
          return Observable.of(false);
        });
    } else {
      return Observable.of(false);
    }
  }

  getToken(): ActToken {
    const actToken = Cookies.get(AppConfigService.tokenCookieName);

    if (actToken) {
      try {
        const decryptToken = <ActToken>JSON.parse(this.utilitiesService.decodeFromCookie(actToken));
        if (decryptToken) {
          return decryptToken;
        }
        this.clearToken();
      } catch (error) {
        this.clearToken();
      }
    }
    return;
  }

  getUserInfo(): UserModel {
    const userInfo = Cookies.get(AppConfigService.userInfoCookieName);
    const userFeaturesEncode = Cookies.get(AppConfigService.featureCookieName);
    const companyFeaturesEncode = Cookies.get(AppConfigService.featureCompanyCookieName);

    if (userInfo) {
      try {
        const decryptUserInfo = <UserModel>JSON.parse(this.utilitiesService.decodeFromCookie(userInfo));
        decryptUserInfo.username = decryptUserInfo.email;
        if (decryptUserInfo) {
          let features = [];
          if (userFeaturesEncode) {
            const userFeatures = JSON.parse(this.utilitiesService.decodeFromCookie(userFeaturesEncode));
            features = [...features, ...userFeatures];
          }
          if (companyFeaturesEncode) {
            const companyFeatures = JSON.parse(this.utilitiesService.decodeFromCookie(companyFeaturesEncode));
            features = [...features, ...companyFeatures];
          }
          decryptUserInfo.features = features;

          return decryptUserInfo;
        }
        this.clearToken();
      } catch (error) {
        this.clearToken();
      }
    }
    return;
  }

  clearVerifyCookie() {
    Cookies.remove(AppConfigService.stateCookieName, { domain: domain });
    Cookies.remove(AppConfigService.codeVerifierCookieName, { domain: domain });
    Cookies.remove(AppConfigService.codeChallengeCookieName, { domain: domain });
  }

  clearToken() {
    Cookies.remove(AppConfigService.userInfoCookieName, { domain: domain });
    Cookies.remove(AppConfigService.tokenCookieName, { domain: domain });
    Cookies.remove(AppConfigService.publicKeyCookieName, { domain: domain });
    Cookies.remove(AppConfigService.featureCookieName, { domain: domain });
    Cookies.remove(AppConfigService.featureCompanyCookieName, { domain: domain });
    Cookies.remove(AppConfigService.notifyDisabledCookieName, { domain: domain });
    Cookies.remove(AppConfigService.bannerClosedCookieName, { domain: domain });
    Cookies.remove(AppConfigService.ssoTokenCookieName, { domain: domain });
    Cookies.remove(AppConfigService.coBookerCookieName, { domain: domain });
    Cookies.remove(AppConfigService.shareBudgetCodeCookieName, { domain: domain });
    Cookies.remove(AppConfigService.languageCookieName, { domain: domain });
    this.clearVerifyCookie();
    try {
      localStorage.setItem(AppConfigService.localStorageSearchHistoryName, JSON.stringify([]));
    } catch (err) {
      Cookies.set(AppConfigService.localStorageSearchHistoryName, JSON.stringify([]), { domain: domain });
    }
  }

  getRequestOptions(headerType: HeaderTypes = HeaderTypes.ACT_TOKEN, contentType: ContentTypes = ContentTypes.JSON, email: string = '', payload?: any): RequestOptions {
    const headers = new Headers();
    switch (contentType) {
      case ContentTypes.JSON:
        headers.append(HeaderKey.contentType, HeaderValue.applicationJson);
        break;
      case ContentTypes.URLENCODE:
        headers.append(HeaderKey.contentType, HeaderValue.applicationWebForm);
        break;
    }

    switch (headerType) {
      case HeaderTypes.EXCLUDE_TOKEN: break;
      case HeaderTypes.AUTHORIZATION_WM_TOKEN:
        const user = this.getToken();
        if (user) {
          headers.append(HeaderKey.wemallAccessToken, user.accessToken.valueOf());
          headers.append(HeaderKey.wemallRefreshAccessToken, user.refreshToken.valueOf());
        }
      // tslint:disable-next-line:no-switch-case-fall-through
      case HeaderTypes.AUTHORIZATION:
        headers.append(HeaderKey.authorization, this.generateAuthorization(email));
        break;
      case HeaderTypes.AUTHORIZATION_RANDOM_STR:
        headers.append(HeaderKey.authorization, this.generateAuthorizationWithRandomString(payload));
        break;
      case HeaderTypes.ACT_TOKEN:
      default:
        const currentUser = this.getToken();
        if (currentUser) {
          headers.append(HeaderKey.actAccessToken, currentUser.accessToken.valueOf());
          headers.append(HeaderKey.actRefreshToken, currentUser.refreshToken.valueOf());
        }
        break;
      case HeaderTypes.SSO_TOKEN:
        const token = this.getSsoToken();
        if (token) {
          headers.append(HeaderKey.actAccessToken, token.accessToken.valueOf());
          headers.append(HeaderKey.actRefreshToken, token.refreshToken.valueOf());
        }
        break;
    }
    return new RequestOptions({ headers: headers });
  }

  generateAuthorization(email: string) {
    return this.utilitiesService.base64Encoder(`${email}:${this.appConfigService.getTimeForAuthorizationHeader()}`);
  }

  generateAuthorizationWithRandomString(payload: any) {
    return Math.random().toString(36).substr(2, 3) + this.utilitiesService.base64Encoder(payload);
  }

  getUserFeatureFromFeature(features: any[]) {
    const keys = Object.keys(FeatureType).map(key => { return FeatureType[key]; });
    return features.filter(feature => { return keys.indexOf(feature.feature) > -1 });
  }

  getCompanyFeatureFromFeature(features: any[]) {
    const keys = Object.keys(CompanyFeatureConfigType).map(key => { return CompanyFeatureConfigType[key]; });
    return features.filter(feature => { return keys.indexOf(feature.feature) > -1 });
  }

  public getExpiresTime() {
    return this.expiresTime;
  }

  generateCodeVerifier() {
    return this.utilitiesService.randomStringWithLength(48);
  }

  generateCodeChallenge(codeVerifier: string) {
    return CryptoJS.SHA256(codeVerifier).toString(CryptoJS.enc.Base64).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  }

  saveCodeVerifier(codeVerifier: string, codeChallenge: string) {
    Cookies.set(AppConfigService.codeVerifierCookieName, this.utilitiesService.encodeToCookie(codeVerifier), { domain: domain });
    Cookies.set(AppConfigService.codeChallengeCookieName, this.utilitiesService.encodeToCookie(codeChallenge), { domain: domain });
  }

  saveState(state: string) {
    Cookies.set(AppConfigService.stateCookieName, this.utilitiesService.encodeToCookie(state), { domain: domain });
  }

  saveSsoToken(token) {
    Cookies.remove(AppConfigService.ssoTokenCookieName, { domain: domain });
    Cookies.set(AppConfigService.ssoTokenCookieName,
      this.utilitiesService.encodeToCookie(JSON.stringify(new ActToken(token.accessToken, token.refreshToken))),
      { expires: this.expiresTime, domain: domain });
  }

  getCodeVerifier() {
    return this.utilitiesService.decodeFromCookie(Cookies.get(AppConfigService.codeVerifierCookieName));
  }

  getState() {
    return this.utilitiesService.decodeFromCookie(Cookies.get(AppConfigService.stateCookieName));
  }

  getSsoToken(): ActToken {
    const ssoToken = Cookies.get(AppConfigService.ssoTokenCookieName, { domain: domain });

    if (ssoToken) {
      try {
        const decryptToken = <ActToken>JSON.parse(this.utilitiesService.decodeFromCookie(ssoToken));
        if (decryptToken) {
          return decryptToken;
        }
        this.clearToken();
      } catch (error) {
        this.clearToken();
      }
    }
    return;
  }

  authorizationRequest(queryParams: AuthorizeRequest): Observable<AuthorizationResponse> {
    const url = `${environment.endpoint.authorizeRequest}${this.utilitiesService.objectToQueryString(queryParams)}`;
    const option = this.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return this.http.get(url, option).map(m => m.json().data);
  }

  getTokenAuthen(code: string, codeVerifier: string): Observable<ActToken> {
    const paramsObj = {
      grant_type: SSO.GRANT_TYPE,
      client_id: SSO.CLIENT_ID,
      code: code,
      code_verifier: codeVerifier,
      code_challenge_method: SSO.CODE_CHALLENGE_METHOD
    };
    const url = `${environment.endpoint.getTokenAuthen}${this.utilitiesService.objectToQueryString(paramsObj)}`;
    const option = this.getRequestOptions(HeaderTypes.SSO_TOKEN, ContentTypes.URLENCODE);
    return this.http.post(url, null, option).map(m => m.json().data);
  }

  allowConsent(scope: ConsentRequest): Observable<any> {
    const url = `${environment.endpoint.consent}`;
    const option = this.getRequestOptions(HeaderTypes.SSO_TOKEN);
    return this.http.post(url, scope, option);
  }

  getUserInformation(companyId?: number, taxAddress?: boolean): Observable<UserModel> {
    const id = companyId ? `companyId=${companyId}` : undefined;
    const tax = taxAddress ? `taxAddress=${taxAddress}` : undefined;
    let param;

    if (id && tax) {
      param = `?${id}&${tax}`;
    } else if (!id && !tax) {
      param = '';
    } else {
      param = `?${id ? id : tax}`;
    }

    return this.validateToken(this.getToken()).flatMap((res) => {
      if (res) {
        const url = `${environment.endpoint.userInfo}` + param;
        const option = this.getRequestOptions(HeaderTypes.ACT_TOKEN);
        return this.http.get(url, option).map((res: Response) => res.json().data);
      } else {
        this.clearToken();
        this.utilitiesService.redirectPage('/', this.appConfigService.getLanguage());
        throw new Error(ActHttpService.unauthorizeMessage);
      }
    });
  }

  generateAuthorizeRequest(codeVerifier?: string, clientId?: string): AuthorizeRequest {
    codeVerifier = codeVerifier || this.generateCodeVerifier();
    const codeChallenge = this.generateCodeChallenge(codeVerifier);
    const state = this.utilitiesService.randomStringWithLength(10);
    const redirect_uri = `${redirectURI}`;
    this.saveCodeVerifier(codeVerifier, codeChallenge);
    this.saveState(state);

    const queryParams = new AuthorizeRequest;
    queryParams.response_type = SSO.RESPONSE_TPYE;
    queryParams.scope = SSO.SCOPE;
    queryParams.client_id = clientId ? clientId : SSO.CLIENT_ID;
    queryParams.state = state;
    queryParams.code_challenge = codeChallenge;
    queryParams.redirect_uri = redirect_uri;
    queryParams.code_challenge_method = SSO.CODE_CHALLENGE_METHOD;
    queryParams.lang = this.appConfigService.getLanguage();
    return queryParams;
  }

  login(companyCode?: string, clientId?: string): Observable<boolean> {
    const codeVerifier = this.generateCodeVerifier();
    const queryParams = this.generateAuthorizeRequest(codeVerifier, clientId);
    return this.authorizationRequest(queryParams)
      .flatMap((res) => {
        const code = res.redirectURI.substring(res.redirectURI.indexOf('code=') + 5, res.redirectURI.indexOf('&state'));
        const state = res.redirectURI.substring(res.redirectURI.indexOf('state=') + 6);
        const isValidState = state === queryParams.state;
        return isValidState ? this.getTokenAuthen(code, codeVerifier) : Observable.throw('Invalid State');
      })
      .flatMap((res) => {
        this.saveToken(new LoginResponseModel(undefined, res));
        return this.getUserInformation();
      })
      .flatMap((user) => {
        user.companyCode = companyCode;
        const loginResponse = new LoginResponseModel(user, this.getToken());
        this.saveToken(loginResponse);
        return Observable.of(loginResponse);
      }).flatMap((loginResponse) => {
        return this.saveUserFeature(loginResponse.token, loginResponse.user, AuthenticationService.features);
      })
      .catch((errer) => {
        console.error(errer);
        return Observable.of(false);
      })
      .finally(() => {
        this.clearVerifyCookie();
      });
  }

  loginFromAccount(state: string, code: string, companyCode?: string): Observable<boolean> {
    if (state === this.getState()) {
      let codeVerifier = this.getCodeVerifier();
      return this.getTokenAuthen(code, codeVerifier)
        .flatMap((res) => {
          this.saveToken(new LoginResponseModel(undefined, res));
          return this.getUserInformation();
        })
        .flatMap((user) => {
          user.companyCode = companyCode;
          const loginResponse = new LoginResponseModel(user, this.getToken());
          this.saveToken(loginResponse);
          return Observable.of(loginResponse);
        }).flatMap((loginResponse) => {
          return this.saveUserFeature(loginResponse.token, loginResponse.user, AuthenticationService.features);
        })
        .catch((errer) => {
          console.error(errer);
          return Observable.of(false);
        })
        .finally(() => {
          this.clearVerifyCookie();
        });
    } else {
      this.clearVerifyCookie();
      return Observable.throw('Invalid State');
    }
  }

  requestOTP(request: OTPRequest): Observable<RequestOTPResponse> {
    const url = `${environment.endpoint.requestOTP}`;
    const options = this.getRequestOptions(HeaderTypes.ACT_TOKEN, ContentTypes.JSON);
    return this.http.post(url, request, options).map(m => m.json().data);
  }

  verifyOTP(request: VerifyOTPRequest): Observable<VerifyOTPResponse> {
    const url = `${environment.endpoint.verifyOTP}`;
    const options = this.getRequestOptions(HeaderTypes.ACT_TOKEN, ContentTypes.JSON);
    return this.http.post(url, request, options).map(m => m.json().data);
  }

  changePhoneNumber(request: ChangePhoneRequest): Observable<ChangePhoneResponse> {
    const url = `${environment.endpoint.userPhone}`;
    const options = this.getRequestOptions(HeaderTypes.ACT_TOKEN, ContentTypes.JSON);
    return this.http.post(url, request, options).map(m => m.json().data);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/authentication/authentication.service.ts